/* ============================================================
   Nawy — Coming Soon (design: "Coming Soon v3")
   Values are ported from the design's inline styles; colours and
   radii resolve to Nawy brand tokens where the design's literals
   match a token exactly.
   ============================================================ */

@import './tokens/colors.css';
@import './tokens/typography.css';
@import './tokens/spacing.css';

:root {
  /* The page pairs the brand's digital typeface with an Arabic companion. */
  --cs-font-latin: 'Poppins', 'IBM Plex Sans Arabic', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  --cs-font-arabic: 'IBM Plex Sans Arabic', 'Poppins', 'Helvetica Neue', Helvetica, Arial, sans-serif;

  /* Translucent whites layered over the dark-blue ground. */
  --cs-hairline: rgba(255, 255, 255, 0.18);
  --cs-body-text: rgba(255, 255, 255, 0.9);
  --cs-footer-text: rgba(255, 255, 255, 0.7);
  --cs-field-bg: rgba(255, 255, 255, 0.1);
  --cs-field-border: rgba(255, 255, 255, 0.22);
  --cs-field-icon: rgba(255, 255, 255, 0.6);
  --cs-placeholder: rgba(255, 255, 255, 0.55);
}

*,
*::before,
*::after {
  box-sizing: border-box;
}

html,
body {
  margin: 0;
  padding: 0;
}

body {
  font-family: var(--cs-font-latin);
  background: var(--nawy-dark-blue);
  color: var(--nawy-white);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* ---------- Layout shell ---------- */

.cs-wrap {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  padding: 32px 56px 28px;
  background: var(--nawy-dark-blue);
  color: var(--nawy-white);
}

/* ---------- Header ---------- */

.cs-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding-bottom: 24px;
  border-bottom: 1px solid var(--cs-hairline);
}

.cs-logo {
  height: clamp(28px, 5vw, 34px);
  width: auto;
}

.cs-domain {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: var(--text-xs);
  font-weight: var(--fw-medium);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--nawy-teal);
}

/* ---------- Main ---------- */

.cs-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 56px 0 48px;
  gap: 40px;
}

.cs-headline-group {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.cs-headline {
  margin: 0;
  font-size: clamp(56px, 11vw, 168px);
  font-weight: var(--fw-bold);
  line-height: 0.95;
  letter-spacing: -0.04em;
  text-transform: uppercase;
  color: var(--nawy-white);
}

.cs-headline-accent {
  color: var(--nawy-teal);
}

.cs-headline-ar {
  margin: 8px 0 0;
  font-family: var(--cs-font-arabic);
  font-size: clamp(28px, 4vw, 56px);
  font-weight: var(--fw-semibold);
  line-height: 1.3;
  color: var(--nawy-white);
}

.cs-bottom {
  display: flex;
  justify-content: center;
  width: 100%;
}

.cs-capture {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  width: 100%;
  max-width: 560px;
}

.cs-lede {
  margin: 0;
  font-size: 17px;
  font-weight: var(--fw-light);
  line-height: var(--lh-relaxed);
  color: var(--cs-body-text);
  text-wrap: pretty;
}

.cs-lede-ar {
  margin: 0;
  font-family: var(--cs-font-arabic);
  font-size: 17px;
  font-weight: var(--fw-regular);
  line-height: 1.8;
  color: var(--cs-body-text);
}

/* ---------- Email capture ---------- */

.cs-form {
  width: 100%;
  display: flex;
  gap: 6px;
  align-items: center;
  text-align: left;
  background: var(--cs-field-bg);
  border: 1px solid var(--cs-field-border);
  border-radius: var(--radius-pill);
  padding: 6px;
}

.cs-field {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  height: 48px;
  padding: 0 18px;
}

.cs-field svg {
  flex: none;
  color: var(--cs-field-icon);
}

.cs-input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-family: var(--cs-font-latin);
  font-size: var(--text-md);
  color: var(--nawy-white);
}

.cs-input::placeholder {
  color: var(--cs-placeholder);
}

.cs-input:focus-visible {
  outline: none;
}

/* Move the focus ring to the whole pill — the input has no border of its own. */
.cs-form:focus-within {
  border-color: var(--nawy-teal);
  box-shadow: var(--shadow-focus);
}

/*
 * Nawy design-system Button, variant="primary" size="lg".
 * Ported from components/buttons/Button.jsx in the brand bundle.
 */
.cs-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: auto;
  height: 54px;
  padding: 0 30px;
  font-family: var(--font-sans);
  font-size: var(--text-lg);
  font-weight: var(--fw-semibold);
  line-height: 1;
  white-space: nowrap;
  color: var(--nawy-white);
  background: var(--action-primary);
  border: 1px solid transparent;
  border-radius: var(--radius-pill);
  cursor: pointer;
  transition:
    background var(--dur-fast) var(--ease-standard),
    transform var(--dur-fast) var(--ease-standard),
    box-shadow var(--dur-fast) var(--ease-standard);
}

.cs-button:hover {
  background: var(--action-primary-hover);
}

.cs-button:active {
  background: var(--action-primary-active);
  transform: scale(0.97);
}

.cs-button:focus-visible {
  outline: none;
  box-shadow: var(--shadow-focus);
}

.cs-button[disabled] {
  cursor: not-allowed;
  opacity: 0.5;
  transform: none;
}

/* ---------- Post-submit states ---------- */

.cs-confirmation {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: var(--text-md);
  font-weight: var(--fw-medium);
  color: var(--nawy-teal);
}

.cs-error {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  font-size: var(--text-sm);
  font-weight: var(--fw-medium);
  line-height: var(--lh-normal);
  color: var(--nawy-light-orange);
  text-align: left;
}

.cs-error svg {
  flex: none;
}

/* ---------- Footer ---------- */

.cs-footer {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding-top: 20px;
  border-top: 1px solid var(--cs-hairline);
  text-align: center;
}

.cs-copyright {
  font-size: var(--text-xs);
  color: var(--cs-footer-text);
}

.cs-social {
  display: flex;
  align-items: center;
  gap: 24px;
  font-size: var(--text-sm);
  font-weight: var(--fw-medium);
}

.cs-social a {
  display: flex;
  align-items: center;
  min-height: 44px;
  color: var(--nawy-white);
  text-decoration: none;
  transition: color var(--dur-fast) var(--ease-standard);
}

.cs-social a:hover,
.cs-social a:focus-visible {
  color: var(--nawy-teal);
}

/* ---------- Responsive ---------- */

@media (max-width: 900px) {
  .cs-wrap {
    padding-left: 24px;
    padding-right: 24px;
  }
}

/*
 * The design is a single full-height screen. At the headline's largest sizes it
 * outgrows a laptop viewport and pushes the logo off the top, so tighten the
 * vertical rhythm once height — not width — becomes the binding constraint.
 */
@media (max-height: 940px) {
  .cs-main {
    padding: 24px 0 20px;
    gap: 24px;
  }

  .cs-headline {
    font-size: clamp(48px, 9vw, 124px);
  }

  .cs-headline-ar {
    font-size: clamp(24px, 3.4vw, 44px);
  }
}

@media (max-height: 720px) {
  .cs-main {
    padding: 16px 0 16px;
    gap: 16px;
  }

  .cs-headline {
    font-size: clamp(40px, 7.5vw, 92px);
  }

  .cs-capture {
    gap: 14px;
  }
}

@media (max-width: 640px) {
  .cs-form {
    flex-direction: column;
    border-radius: var(--radius-xl);
    padding: 12px;
    gap: 12px;
  }

  .cs-form > * {
    width: 100% !important;
    flex: none !important;
  }

  .cs-field {
    background: rgba(255, 255, 255, 0.08);
    border-radius: var(--radius-pill);
  }

  .cs-social {
    flex-wrap: wrap;
    justify-content: center;
  }
}

@media (prefers-reduced-motion: reduce) {
  .cs-button {
    transition: none;
  }

  .cs-button:active {
    transform: none;
  }
}
